import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/Table';
import { Badge } from './ui/Badge';
import { Input } from './ui/Input';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatCurrency, cn } from '../lib/utils';
import { DollarSign, FileText, TrendingUp, Users, Search, ArrowUpRight, ArrowDownRight, ChevronDown } from 'lucide-react';
import { MOCK_USERS, LEAD_SOURCES } from '../data/mockData';
interface OverviewProps {
  onSelectUser?: (user: {
    id: string;
    model: 'BPS' | 'CDS';
  }) => void;
}
export function Overview({
  onSelectUser
}: OverviewProps) {
  const [search, setSearch] = useState('');
  // Available months (newest first) — shared across all users
  const months = MOCK_USERS[0]?.monthlyLoans ?? [];
  const [selectedMonthKey, setSelectedMonthKey] = useState<string>(months[0]?.key ?? '');
  const selectedMonth = months.find(m => m.key === selectedMonthKey) ?? months[0];
  // Calculate derived metrics for the selected month
  const metrics = useMemo(() => {
    const selectedIndex = months.findIndex(m => m.key === selectedMonthKey);
    const priorKey = months[selectedIndex + 1]?.key;
    let totalCommissions = 0;
    let totalFunded = 0;
    let priorFunded = 0;
    const sourceVolumes: Record<string, {
      files: number;
      volume: number;
    }> = {};
    LEAD_SOURCES.forEach(s => sourceVolumes[s] = {
      files: 0,
      volume: 0
    });
    const usersWithCommissions = MOCK_USERS.map(user => {
      const monthLoans = user.monthlyLoans.find(m => m.key === selectedMonthKey)?.loans ?? [];
      const priorLoans = priorKey ? user.monthlyLoans.find(m => m.key === priorKey)?.loans ?? [] : [];
      let commission = 0;
      monthLoans.forEach(loan => {
        commission += loan.commissionAmount;
        if (sourceVolumes[loan.leadSource]) {
          sourceVolumes[loan.leadSource].files += 1;
          sourceVolumes[loan.leadSource].volume += loan.loanAmount;
        }
      });
      totalCommissions += commission;
      totalFunded += monthLoans.length;
      priorFunded += priorLoans.length;
      return {
        ...user,
        fundedSelected: monthLoans.length,
        fundedPrior: priorLoans.length,
        estimatedCommission: commission
      };
    });
    const topSources = Object.entries(sourceVolumes).map(([name, data]) => ({
      name,
      ...data
    })).filter(s => s.files > 0).sort((a, b) => b.files - a.files).slice(0, 6); // Show top 6 in cockpit
    return {
      totalCommissions,
      totalFunded,
      lastMonthFunded: priorFunded,
      hasPrior: Boolean(priorKey),
      fundedTrend: (totalFunded - priorFunded) / Math.max(1, priorFunded) * 100,
      bpsUsers: MOCK_USERS.filter(u => u.model === 'BPS').length,
      cdsUsers: MOCK_USERS.filter(u => u.model === 'CDS').length,
      topSources,
      users: usersWithCommissions.sort((a, b) => b.estimatedCommission - a.estimatedCommission)
    };
  }, [months, selectedMonthKey]);
  const filteredUsers = metrics.users.filter(u => u.name.toLowerCase().includes(search.toLowerCase()) || u.title.toLowerCase().includes(search.toLowerCase()));
  // Mock monthly trending data
  const monthlyData = [{
    name: 'Jan',
    bpsCommissions: 450000,
    cdsCommissions: 120000
  }, {
    name: 'Feb',
    bpsCommissions: 520000,
    cdsCommissions: 145000
  }, {
    name: 'Mar',
    bpsCommissions: 480000,
    cdsCommissions: 110000
  }, {
    name: 'Apr',
    bpsCommissions: 610000,
    cdsCommissions: 180000
  }, {
    name: 'May',
    bpsCommissions: 590000,
    cdsCommissions: 165000
  }, {
    name: 'Jun',
    bpsCommissions: 680000,
    cdsCommissions: 210000
  }];
  return <div className="space-y-6" data-id="element-440">
      <div className="flex items-center justify-between pb-2" data-id="element-441">
        <div data-id="element-442">
          <h1 className="text-2xl font-semibold text-slate-900" data-id="element-443">
            Commission Manager
          </h1>
          <p className="text-sm text-slate-500" data-id="element-444">
            Org-wide performance for {selectedMonth?.label ?? 'this month'}
          </p>
        </div>
        <div className="relative shrink-0" data-id="element-445">
          <label htmlFor="overview-month-select" className="sr-only" data-id="element-446">
            Select month
          </label>
          <select id="overview-month-select" value={selectedMonthKey} onChange={e => setSelectedMonthKey(e.target.value)} className="appearance-none h-9 rounded-lg border border-slate-200 bg-white pl-3 pr-9 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus:ring-1 focus:ring-blue-400 cursor-pointer" data-id="element-447">
            {months.map(m => <option key={m.key} value={m.key} data-id="element-448">
                {m.label}
              </option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-449" />
        </div>
      </div>

      {/* Row 1: KPI Cockpit */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-id="element-450">
        <Card className="bg-white shadow-sm border-slate-200/60 rounded-xl" data-id="element-451">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-452">
            <CardTitle className="text-sm font-medium text-slate-600" data-id="element-453">
              Total Commissions
            </CardTitle>
            <DollarSign className="h-4 w-4 text-emerald-600" data-id="element-454" />
          </CardHeader>
          <CardContent data-id="element-455">
            <div className="text-2xl font-bold text-slate-900" data-id="element-456">
              {formatCurrency(metrics.totalCommissions)}
            </div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-457">
              Projected across all users
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm border-slate-200/60 rounded-xl" data-id="element-458">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-459">
            <CardTitle className="text-sm font-medium text-slate-600" data-id="element-460">
              Funded Files
            </CardTitle>
            <FileText className="h-4 w-4 text-blue-600" data-id="element-461" />
          </CardHeader>
          <CardContent data-id="element-462">
            <div className="text-2xl font-bold text-slate-900" data-id="element-463">
              {metrics.totalFunded}
            </div>
            <div className="flex items-center text-xs mt-1" data-id="element-464">
              {metrics.fundedTrend >= 0 ? <span className="text-emerald-600 flex items-center font-semibold bg-emerald-50 px-1.5 py-0.5 rounded-sm" data-id="element-465">
                  <ArrowUpRight className="h-3 w-3 mr-1" data-id="element-466" />{' '}
                  {metrics.fundedTrend.toFixed(1)}%
                </span> : <span className="text-red-600 flex items-center font-semibold bg-red-50 px-1.5 py-0.5 rounded-sm" data-id="element-467">
                  <ArrowDownRight className="h-3 w-3 mr-1" data-id="element-468" />{' '}
                  {Math.abs(metrics.fundedTrend).toFixed(1)}%
                </span>}
              <span className="text-slate-500 ml-2" data-id="element-469">vs last month</span>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm border-slate-200/60 rounded-xl" data-id="element-470">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-471">
            <CardTitle className="text-sm font-medium text-slate-600" data-id="element-472">
              Active Users
            </CardTitle>
            <Users className="h-4 w-4 text-blue-600" data-id="element-473" />
          </CardHeader>
          <CardContent data-id="element-474">
            <div className="text-2xl font-bold text-slate-900" data-id="element-475">
              {MOCK_USERS.length}
            </div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-476">
              {metrics.bpsUsers} BPS | {metrics.cdsUsers} CDS
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white shadow-sm border-slate-200/60 rounded-xl" data-id="element-477">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2" data-id="element-478">
            <CardTitle className="text-sm font-medium text-slate-600" data-id="element-479">
              Top Lead Source
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-blue-600" data-id="element-480" />
          </CardHeader>
          <CardContent data-id="element-481">
            <div className="text-2xl font-bold text-slate-900 truncate" data-id="element-482">
              {metrics.topSources[0]?.name || 'N/A'}
            </div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-483">
              {metrics.topSources[0]?.files || 0} files MTD
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Charts & Sources */}
      <div className="grid gap-6 lg:grid-cols-3" data-id="element-484">
        <Card className="lg:col-span-2 shadow-sm border-slate-200/60" data-id="element-485">
          <CardHeader data-id="element-486">
            <CardTitle data-id="element-487">Commission Trending</CardTitle>
            <CardDescription data-id="element-488">
              Monthly breakdown of BPS vs CDS commissions
            </CardDescription>
          </CardHeader>
          <CardContent className="pl-0" data-id="element-489">
            <div className="h-[350px] w-full" data-id="element-490">
              <ResponsiveContainer width="100%" height="100%" data-id="element-491">
                <BarChart data={monthlyData} margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-492">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" data-id="element-493" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                  fill: '#64748b',
                  fontSize: 12
                }} dy={10} data-id="element-494" />
                  <YAxis axisLine={false} tickLine={false} tick={{
                  fill: '#64748b',
                  fontSize: 12
                }} tickFormatter={value => `$${value / 1000}k`} dx={-10} data-id="element-495" />
                  <Tooltip formatter={(value: number) => formatCurrency(value)} cursor={{
                  fill: '#f8fafc'
                }} contentStyle={{
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  fontSize: '14px'
                }} data-id="element-496" />
                  <Legend wrapperStyle={{
                  paddingTop: '20px'
                }} iconType="circle" data-id="element-497" />
                  <Bar dataKey="bpsCommissions" name="BPS Commissions" stackId="a" fill="#0f172a" radius={[0, 0, 4, 4]} maxBarSize={60} data-id="element-498" />
                  <Bar dataKey="cdsCommissions" name="CDS Commissions" stackId="a" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={60} data-id="element-499" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-slate-200/60 flex flex-col" data-id="element-500">
          <CardHeader data-id="element-501">
            <CardTitle data-id="element-502">Top Lead Sources</CardTitle>
            <CardDescription data-id="element-503">MTD performance by volume</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto" data-id="element-504">
            <div className="space-y-5" data-id="element-505">
              {metrics.topSources.map((source, idx) => <div key={source.name} className="flex items-center group" data-id="element-506">
                  <div className="w-6 text-sm font-bold text-slate-300 group-hover:text-slate-400 transition-colors" data-id="element-507">
                    {idx + 1}
                  </div>
                  <div className="space-y-1 flex-1" data-id="element-508">
                    <p className="text-sm font-semibold text-slate-900 leading-none" data-id="element-509">
                      {source.name}
                    </p>
                    <p className="text-xs text-slate-500 font-medium" data-id="element-510">
                      {formatCurrency(source.volume)} vol
                    </p>
                  </div>
                  <div className="font-semibold text-sm text-slate-700 bg-slate-100 px-2.5 py-1 rounded-md" data-id="element-511">
                    {source.files}{' '}
                    <span className="text-slate-500 font-normal text-xs" data-id="element-512">
                      files
                    </span>
                  </div>
                </div>)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Leaderboard */}
      <Card className="flex flex-col h-[600px] shadow-sm border-slate-200/60" data-id="element-513">
        <CardHeader className="border-b border-slate-100 shrink-0 flex flex-row items-center justify-between pb-4 bg-white rounded-t-xl" data-id="element-514">
          <div data-id="element-515">
            <CardTitle data-id="element-516">User Leaderboard</CardTitle>
            <CardDescription data-id="element-517">
              All users ranked by projected commission
            </CardDescription>
          </div>
          <div className="relative w-72" data-id="element-518">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-519" />
            <Input placeholder="Search users by name or title..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 bg-slate-50 border-slate-200 focus:bg-white transition-colors" data-id="element-520" />
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto p-0" data-id="element-521">
          <Table data-id="element-522">
            <TableHeader className="sticky top-0 bg-slate-50/90 backdrop-blur-sm shadow-sm z-10 border-b border-slate-200" data-id="element-523">
              <TableRow className="hover:bg-transparent" data-id="element-524">
                <TableHead className="pl-6 w-[350px] font-semibold text-slate-600" data-id="element-525">
                  User
                </TableHead>
                <TableHead className="font-semibold text-slate-600" data-id="element-526">
                  Model
                </TableHead>
                <TableHead className="text-right font-semibold text-slate-600" data-id="element-527">
                  Funded (MTD)
                </TableHead>
                <TableHead className="text-right font-semibold text-slate-600" data-id="element-528">
                  Trend
                </TableHead>
                <TableHead className="text-right pr-6 font-semibold text-slate-600" data-id="element-529">
                  Projected Commission
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-id="element-530">
              {filteredUsers.map(user => {
              const trend = user.fundedPrior > 0 ? (user.fundedSelected - user.fundedPrior) / user.fundedPrior * 100 : 0;
              return <TableRow key={user.id} onClick={() => onSelectUser?.({
                id: user.id,
                model: user.model
              })} role="button" tabIndex={0} onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectUser?.({
                    id: user.id,
                    model: user.model
                  });
                }
              }} className="hover:bg-blue-50/50 transition-colors cursor-pointer focus:outline-none focus:bg-blue-50/60 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-blue-400" data-id="element-531">
                    <TableCell className="pl-6 py-3" data-id="element-532">
                      <div className="flex items-center gap-4" data-id="element-533">
                        <img src={user.avatar} alt={user.name} className="h-10 w-10 rounded-full bg-slate-200 shadow-sm border border-slate-100" data-id="element-534" />
                        <div data-id="element-535">
                          <div className="font-semibold text-sm text-slate-900" data-id="element-536">
                            {user.name}
                          </div>
                          <div className="text-xs text-slate-500 font-medium" data-id="element-537">
                            {user.title}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell data-id="element-538">
                      <Badge variant={user.model === 'BPS' ? 'default' : 'secondary'} className={cn('text-[10px] uppercase tracking-wider font-bold px-2 py-0.5', user.model === 'BPS' ? 'bg-slate-900 text-white' : 'bg-blue-100 text-blue-700 hover:bg-blue-100')} data-id="element-539">
                        {user.model}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-slate-700 text-base" data-id="element-540">
                      {user.fundedSelected}
                    </TableCell>
                    <TableCell className="text-right" data-id="element-541">
                      <div className="flex items-center justify-end text-xs" data-id="element-542">
                        {trend > 0 ? <span className="text-emerald-600 flex items-center font-bold bg-emerald-50 px-2 py-1 rounded-md" data-id="element-543">
                            <ArrowUpRight className="h-3 w-3 mr-1" data-id="element-544" />{' '}
                            {trend.toFixed(0)}%
                          </span> : trend < 0 ? <span className="text-red-600 flex items-center font-bold bg-red-50 px-2 py-1 rounded-md" data-id="element-545">
                            <ArrowDownRight className="h-3 w-3 mr-1" data-id="element-546" />{' '}
                            {Math.abs(trend).toFixed(0)}%
                          </span> : <span className="text-slate-400 font-medium px-2 py-1" data-id="element-547">
                            -
                          </span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right pr-6 font-bold text-slate-900 text-base" data-id="element-548">
                      {formatCurrency(user.estimatedCommission)}
                    </TableCell>
                  </TableRow>;
            })}
              {filteredUsers.length === 0 && <TableRow data-id="element-549">
                  <TableCell colSpan={5} className="h-32 text-center text-slate-500" data-id="element-550">
                    <div className="flex flex-col items-center justify-center" data-id="element-551">
                      <Search className="h-8 w-8 text-slate-300 mb-2" data-id="element-552" />
                      <p data-id="element-553">No users found matching "{search}"</p>
                    </div>
                  </TableCell>
                </TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>;
}